/**
 * Standard 40-card mix used by the deck builder auto-fill and by the
 * random-deck event queue. Win-rate ranking is optional; pass `{}` to
 * shuffle every bucket.
 */

export const STANDARD_DECK = {
  lowLevelMonsters: 15,
  highLevelMonsters: 5,
  spellTrapSlots: 20,
} as const

/**
 * Random-deck event only. There is one Field Spell on the whole board, and
 * Equips need a monster, so the mix keeps these kinds small.
 */
export const RANDOM_DECK_KIND_CAPS = {
  field: 2,
  equip: 2,
} as const

export type RandomDeckKindCaps = {
  field: number
  equip: number
}

/**
 * Monster slots inside the 20-monster random mix. Flip, tributes, then the
 * rest as level 4 or lower. Auto-fill in the builder still uses STANDARD_DECK.
 */
export const RANDOM_DECK_MONSTER_MIX = {
  flip: 3,
  level7Plus: 1,
  level5or6: 2,
  otherLow: 14,
} as const

export type RandomDeckMonsterMix = {
  flip: number
  level7Plus: number
  level5or6: number
  otherLow: number
}

export type StandardDeckMix = {
  lowLevelMonsters: number
  highLevelMonsters: number
  spellTrapSlots: number
}

/** Settled duels before a win rate counts. Matches the Meta page. */
export const AUTO_DECK_MIN_WIN_SAMPLE = 8

const MAX_COPIES = 3
const MAIN_MAX = 60
const EXTRA_MAX = 15

export interface StandardDeckCard {
  id: string | number
  type?: string
  frameType?: string
  typeline?: string[]
  race?: string
  level?: number
  atk?: number
}

/** Wins and settled duels for one card, from the season meta. */
export interface CardWinSample {
  wins: number
  results: number
}

export type StandardDeckRng = () => number

export interface BuildStandardDeckOpts {
  rates?: Readonly<Record<string, CardWinSample>>
  copyLimits?: Readonly<Record<string, number>>
  rng?: StandardDeckRng
  /** Override the 15 / 5 / 20 mix. Omitted keys keep {@link STANDARD_DECK}. */
  mix?: Partial<StandardDeckMix>
  /**
   * Main buckets take one copy per pass (more names). Default false matches
   * the live random-deck event: dump owned copies of each picked name.
   */
  spreadMain?: boolean
  /** Total Field / Equip Spell copies allowed in the Main Deck. */
  kindCaps?: RandomDeckKindCaps
  /** When set, replaces the 15 / 5 monster split with Flip and tribute buckets. */
  monsterMix?: RandomDeckMonsterMix
}

const shuffle = <T,>(items: T[], rng: StandardDeckRng): T[] => {
  const next = items.slice()
  for (let i = next.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1))
    const a = next[i]
    const b = next[j]
    if (a === undefined || b === undefined) continue
    next[i] = b
    next[j] = a
  }
  return next
}

const cardId = (card: StandardDeckCard): string => String(card.id)

const isFusion = (card: StandardDeckCard): boolean =>
  card.frameType === 'fusion' || card.typeline?.includes('Fusion') === true

const isMonster = (card: StandardDeckCard): boolean => {
  if (isFusion(card)) return false
  if (card.typeline?.includes('Monster')) return true
  return String(card.type || '').toLowerCase().includes('monster')
}

export function isFlipMonsterCard(card: StandardDeckCard): boolean {
  if (!isMonster(card)) return false
  if (card.typeline?.includes('Flip')) return true
  return String(card.type || '').toLowerCase().includes('flip')
}

function monsterLevel(card: StandardDeckCard): number {
  return typeof card.level === 'number' && Number.isFinite(card.level) ? card.level : 0
}

const isSpell = (card: StandardDeckCard): boolean => {
  if (card.typeline?.includes('Spell')) return true
  const type = String(card.type || '').toLowerCase()
  return type.includes('spell') || type.includes('magic')
}

const isTrap = (card: StandardDeckCard): boolean => {
  if (card.typeline?.includes('Trap')) return true
  return String(card.type || '').toLowerCase().includes('trap')
}

const spellRace = (card: StandardDeckCard): string => {
  if (card.race) return card.race
  const line = card.typeline?.find((token) => token === 'Field' || token === 'Equip')
  return line ?? ''
}

export function isFieldSpellCard(card: StandardDeckCard): boolean {
  if (!isSpell(card)) return false
  if (spellRace(card) === 'Field') return true
  return String(card.type || '').toLowerCase().includes('field')
}

export function isEquipSpellCard(card: StandardDeckCard): boolean {
  if (!isSpell(card) || isFieldSpellCard(card)) return false
  if (spellRace(card) === 'Equip') return true
  return String(card.type || '').toLowerCase().includes('equip')
}

type KindCount = { field: number; equip: number }

function kindRoom(
  card: StandardDeckCard,
  count: KindCount,
  caps: RandomDeckKindCaps | undefined,
): number {
  if (!caps) return Number.POSITIVE_INFINITY
  if (isFieldSpellCard(card)) return Math.max(0, caps.field - count.field)
  if (isEquipSpellCard(card)) return Math.max(0, caps.equip - count.equip)
  return Number.POSITIVE_INFINITY
}

function noteKind(card: StandardDeckCard, count: KindCount, n: number): void {
  if (isFieldSpellCard(card)) count.field += n
  else if (isEquipSpellCard(card)) count.equip += n
}

const ownedCopies = (
  card: StandardDeckCard,
  owned: Record<string, number>,
  copyLimits: Readonly<Record<string, number>>,
): number => {
  const house = copyLimits[cardId(card)]
  const cap = house === 1 || house === 2 ? house : MAX_COPIES
  return Math.min(cap, owned[cardId(card)] ?? 0)
}

/**
 * How many Main Deck copies the pool can legally play (owned, copy caps, no Extra).
 */
export function ownedMainCopyCount(
  catalog: StandardDeckCard[],
  owned: Record<string, number>,
  copyLimits: Readonly<Record<string, number>> = {},
): number {
  let total = 0
  for (const card of catalog) {
    if (isFusion(card)) continue
    total += ownedCopies(card, owned, copyLimits)
  }
  return total
}

/**
 * Synthetic collection: one copy of every catalog name.
 * The random-deck event uses this and ignores the house banlist.
 */
export function catalogOneCopyPool(catalog: StandardDeckCard[]): Record<string, number> {
  const owned: Record<string, number> = {}
  for (const card of catalog) owned[cardId(card)] = 1
  return owned
}

/**
 * Synthetic collection: every catalog name at the house cap (or 3).
 * Used by the deck-builder auto-fill.
 */
export function catalogCopyPool(
  catalog: StandardDeckCard[],
  copyLimits: Readonly<Record<string, number>> = {},
): Record<string, number> {
  const owned: Record<string, number> = {}
  for (const card of catalog) {
    const id = cardId(card)
    const house = copyLimits[id]
    owned[id] = house === 1 || house === 2 ? house : MAX_COPIES
  }
  return owned
}

function sampleOf(
  card: StandardDeckCard,
  rates: Readonly<Record<string, CardWinSample>>,
): CardWinSample | null {
  const row = rates[cardId(card)]
  if (!row || !Number.isFinite(row.results) || row.results < AUTO_DECK_MIN_WIN_SAMPLE) {
    return null
  }
  if (!Number.isFinite(row.wins) || row.wins < 0) return null
  return row
}

function atkOf(card: StandardDeckCard): number {
  return typeof card.atk === 'number' && Number.isFinite(card.atk) ? card.atk : 0
}

function rankPool<T extends StandardDeckCard>(
  pool: T[],
  rates: Readonly<Record<string, CardWinSample>>,
  rng: StandardDeckRng,
): T[] {
  const known: T[] = []
  const unknown: T[] = []
  for (const card of pool) {
    if (sampleOf(card, rates)) known.push(card)
    else unknown.push(card)
  }
  known.sort((a, b) => {
    const sa = sampleOf(a, rates)
    const sb = sampleOf(b, rates)
    if (!sa || !sb) return 0
    const aw = sa.wins / sa.results
    const bw = sb.wins / sb.results
    if (aw !== bw) return bw - aw
    if (sa.results !== sb.results) return sb.results - sa.results
    const atk = atkOf(b) - atkOf(a)
    if (atk !== 0) return atk
    return cardId(a).localeCompare(cardId(b))
  })
  return [...known, ...shuffle(unknown, rng)]
}

const takeRanked = <T extends StandardDeckCard>(
  pool: T[],
  want: number,
  used: Map<string, number>,
  owned: Record<string, number>,
  rates: Readonly<Record<string, CardWinSample>>,
  copyLimits: Readonly<Record<string, number>>,
  rng: StandardDeckRng,
  spread: boolean,
  kinds: KindCount,
  kindCaps: RandomDeckKindCaps | undefined,
): T[] => {
  const ranked = rankPool(pool, rates, rng)
  const picked: T[] = []
  const passes = spread ? MAX_COPIES : 1
  for (let pass = 0; pass < passes && picked.length < want; pass++) {
    for (const card of ranked) {
      if (picked.length >= want) break
      const id = cardId(card)
      const have = ownedCopies(card, owned, copyLimits)
      const taken = used.get(id) ?? 0
      if (taken >= have) continue
      const room = kindRoom(card, kinds, kindCaps)
      if (room <= 0) continue
      const n = spread
        ? 1
        : Math.min(have - taken, want - picked.length, room)
      if (n <= 0) continue
      for (let i = 0; i < n; i++) {
        used.set(id, (used.get(id) ?? 0) + 1)
        picked.push({ ...card })
      }
      noteKind(card, kinds, n)
    }
  }
  return picked
}

const leftoverMain = <T extends StandardDeckCard>(
  catalog: T[],
  used: Map<string, number>,
  owned: Record<string, number>,
  rates: Readonly<Record<string, CardWinSample>>,
  copyLimits: Readonly<Record<string, number>>,
  rng: StandardDeckRng,
): T[] => {
  const names: T[] = []
  for (const card of catalog) {
    if (isFusion(card)) continue
    const left = ownedCopies(card, owned, copyLimits) - (used.get(cardId(card)) ?? 0)
    if (left > 0) names.push(card)
  }
  const ranked = rankPool(names, rates, rng)
  const pool: T[] = []
  for (const card of ranked) {
    const left = ownedCopies(card, owned, copyLimits) - (used.get(cardId(card)) ?? 0)
    for (let i = 0; i < left; i++) pool.push(card)
  }
  return pool
}

const orderMain = <T extends StandardDeckCard>(cards: T[]): T[] => {
  const monsters: T[] = []
  const spells: T[] = []
  const traps: T[] = []
  const rest: T[] = []
  for (const card of cards) {
    if (isMonster(card)) monsters.push(card)
    else if (isSpell(card)) spells.push(card)
    else if (isTrap(card)) traps.push(card)
    else rest.push(card)
  }
  return [...monsters, ...spells, ...traps, ...rest]
}

function mixCount(value: unknown, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback
  return Math.max(0, Math.min(MAIN_MAX, Math.floor(value)))
}

function resolveMix(partial: Partial<StandardDeckMix> | undefined): StandardDeckMix {
  return {
    lowLevelMonsters: mixCount(partial?.lowLevelMonsters, STANDARD_DECK.lowLevelMonsters),
    highLevelMonsters: mixCount(partial?.highLevelMonsters, STANDARD_DECK.highLevelMonsters),
    spellTrapSlots: mixCount(partial?.spellTrapSlots, STANDARD_DECK.spellTrapSlots),
  }
}

function resolveMonsterMix(
  partial: RandomDeckMonsterMix | undefined,
): RandomDeckMonsterMix | undefined {
  if (!partial) return undefined
  return {
    flip: mixCount(partial.flip, RANDOM_DECK_MONSTER_MIX.flip),
    level7Plus: mixCount(partial.level7Plus, RANDOM_DECK_MONSTER_MIX.level7Plus),
    level5or6: mixCount(partial.level5or6, RANDOM_DECK_MONSTER_MIX.level5or6),
    otherLow: mixCount(partial.otherLow, RANDOM_DECK_MONSTER_MIX.otherLow),
  }
}

/**
 * Standard list from a copy pool. Extra Deck gets Fusions (up to 15).
 * Empty `rates` fills every bucket at random. Returns fewer than the mix
 * total only when the pool cannot legally reach it.
 */
export function buildStandardDeck<T extends StandardDeckCard>(
  catalog: T[],
  owned: Record<string, number>,
  opts: BuildStandardDeckOpts = {},
): { main: T[]; extra: T[] } {
  const rates = opts.rates ?? {}
  const copyLimits = opts.copyLimits ?? {}
  const rng = opts.rng ?? Math.random
  const mix = resolveMix(opts.mix)
  const monsterMix = resolveMonsterMix(opts.monsterMix)
  const spreadMain = opts.spreadMain === true
  const kindCaps = opts.kindCaps
  const kinds: KindCount = { field: 0, equip: 0 }
  const monsterSlots = monsterMix
    ? monsterMix.flip + monsterMix.level7Plus + monsterMix.level5or6 + monsterMix.otherLow
    : mix.lowLevelMonsters + mix.highLevelMonsters
  const wantMain = Math.min(MAIN_MAX, monsterSlots + mix.spellTrapSlots)
  const used = new Map<string, number>()
  const mains = catalog.filter((card) => !isFusion(card))
  const monsters = mains.filter(isMonster)
  const low = monsters.filter((card) => monsterLevel(card) <= 4)
  const high = monsters.filter((card) => monsterLevel(card) >= 5)
  const spellTraps = mains.filter(
    (card) => (isSpell(card) || isTrap(card)) && ownedCopies(card, owned, copyLimits) > 0,
  )
  const fusions = catalog.filter((card) => isFusion(card))

  const take = (pool: T[], want: number): T[] =>
    takeRanked(
      pool,
      want,
      used,
      owned,
      rates,
      copyLimits,
      rng,
      spreadMain,
      kinds,
      kindCaps,
    )

  const main: T[] = monsterMix
    ? [
        ...take(
          monsters.filter((card) => monsterLevel(card) >= 7),
          monsterMix.level7Plus,
        ),
        ...take(
          monsters.filter(
            (card) => monsterLevel(card) >= 5 && monsterLevel(card) <= 6,
          ),
          monsterMix.level5or6,
        ),
        ...take(
          monsters.filter(
            (card) => isFlipMonsterCard(card) && monsterLevel(card) <= 4,
          ),
          monsterMix.flip,
        ),
        ...take(
          low.filter((card) => !isFlipMonsterCard(card)),
          monsterMix.otherLow,
        ),
        ...take(spellTraps, mix.spellTrapSlots),
      ]
    : [
        ...take(low, mix.lowLevelMonsters),
        ...take(high, mix.highLevelMonsters),
        ...take(spellTraps, mix.spellTrapSlots),
      ]

  while (main.length < wantMain) {
    const fill = leftoverMain(catalog, used, owned, rates, copyLimits, rng)
    const card = fill.find((row) => kindRoom(row, kinds, kindCaps) > 0)
    if (!card) break
    const id = cardId(card)
    used.set(id, (used.get(id) ?? 0) + 1)
    noteKind(card, kinds, 1)
    main.push({ ...card })
  }
  if (main.length > wantMain) main.length = wantMain

  const extraUsed = new Map<string, number>()
  const extra = takeRanked(
    fusions,
    EXTRA_MAX,
    extraUsed,
    owned,
    rates,
    copyLimits,
    rng,
    true,
    { field: 0, equip: 0 },
    undefined,
  )
  return { main: orderMain(main), extra }
}

/**
 * Full-catalog Standard list, one copy of each name, no house banlist,
 * no win-rate weights. Same path the random-deck event queue uses.
 */
export function buildRandomEventDeck<T extends StandardDeckCard>(
  catalog: T[],
  opts: Omit<BuildStandardDeckOpts, 'rates' | 'copyLimits' | 'spreadMain'> = {},
): { main: T[]; extra: T[] } {
  const built: BuildStandardDeckOpts = {
    rates: {},
    copyLimits: {},
    kindCaps: opts.kindCaps ?? {
      field: RANDOM_DECK_KIND_CAPS.field,
      equip: RANDOM_DECK_KIND_CAPS.equip,
    },
    monsterMix: opts.monsterMix ?? { ...RANDOM_DECK_MONSTER_MIX },
  }
  if (opts.rng) built.rng = opts.rng
  if (opts.mix) built.mix = opts.mix
  return buildStandardDeck(catalog, catalogOneCopyPool(catalog), built)
}
